/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "./CSS/WorkspaceDetailScreen.css";
import backIcon from "../../assets/images/back-icon.svg";
import eventShareIcon from "../../assets/images/event-share-button.svg";
import calendarIcon from "../../assets/images/calendar-icon.svg";
import locationIcon from "../../assets/images/location-icon.svg";
import appTextIcon from "../../assets/images/header-text-icon.svg";
import whatsappIcon from "../../assets/images/whatsapp-icon.svg";
import paymentInfoIcon from "../../assets/images/membership-payment-info-icon.svg";
import shareIcon from "../../assets/images/reserved-share-icon.svg";
import tickIcon from "../../assets/images/reservation-tick-icon.svg";
import appIcon from "../../assets/images/header-sabehub-app-icon.svg";
import setupArrow from "../../assets/images/focus-house-setup-arrow.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReserveAcknowledgementDialog from "components/elements/ReserveAcknowledgementDialog";
import ImageCarousel from "components/elements/ImageCarousel";
import {
  checkNonMemberCredits,
  formatDateString,
  formatWorkspaceTimeRange,
  getCalendarRealLifeImages,
  getIsActiveMemberStatus,
  getTermaAndConditionsText,
  getWorkspaceSlotDuration,
  isEventStarted,
  safeParse,
} from "utils/utils";
import {
  CALENDAR_RESERVATION_TABS,
  CALENDAR_RESERVATION_TYPE,
  EXPERIENCE_TYPE,
  MEMBERSHIP_TYPE,
  MENU_ITEM,
  PURCHASE_TYPE,
} from "utils/constants";
import ROUTE_PATHS from "utils/routePaths";
import useWSMApi from "services/api/useWSMApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import SnackbarAlert from "components/elements/SnackbarAlert";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import { STRING, WEB_LINK } from "utils/strings";
import { useLogout } from "services/LogoutProvider";
import { useStorage } from "services/StorageProvider";
import { useTabBar } from "services/TabBarProvider";
import InformationPopup from "components/elements/InformationPopup";
import CancelReservationPopup from "components/elements/CancelReservationPopup";
import {
  trackDetailView,
  trackEvent,
  trackReserveClick,
  trackSlotSelected,
} from "utils/analytics";
import ExpandableView from "components/elements/ExpandableView";
import DeeplinkLoginDialog from "components/elements/DeeplinkLoginDialog";
import StageProgressView from "components/elements/StageProgressView";

const WorkspaceDetailScreen: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slotId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const deepLinkReserve = searchParams.get("reserve");
  const { setSelectedTabBar } = useTabBar();
  const { userProfileData, membershipListData } = useStorage();
  const { handleAppExit } = useLogout();
  const [selectedReservationData, setSelectedReservationData] =
    useState<any>(null);
  const [showAcknowledgementDialog, setShowAcknowledgementDialog] =
    useState<boolean>(false);
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);
  const [showInformationPopup, setShowInformationPopup] =
    useState<boolean>(false);
  const [showFounderOnlyPopup, setShowFounderOnlyPopup] =
    useState<boolean>(false);
  const [showReservationCancelPopup, setShowReservationCancelPopup] =
    useState<boolean>(false);
  const [showDeeplinkLoginDialog, setShowDeeplinkLoginDialog] =
    useState<boolean>(false);
  const {
    reserveWSMSlot,
    cancelReservation,
    fetchWSMSlotById,
    fetchReservationBySlotId,
    sendEventReserveLead,
  } = useWSMApi();
  const [isReserved, setIsReserved] = useState<boolean>(false);
  const isActiveMember = getIsActiveMemberStatus(userProfileData);

  const { data: selectedSlotData } = useQuery({
    queryKey: ["get-slot-data", slotId],
    queryFn: () => fetchWSMSlotById(slotId ?? ""),
    enabled: !!slotId,
  });

  const { data: getReservationData } = useQuery({
    queryKey: ["get-reservation-data", slotId],
    queryFn: () => fetchReservationBySlotId(slotId ?? ""),
    enabled: !!userProfileData,
  });

  const sendEventReserveLeadMutation = useMutation({
    mutationFn: (data: any) => sendEventReserveLead(data),
  });

  useEffect(() => {
    if (getReservationData) {
      setSelectedReservationData(getReservationData);
      setIsReserved(!getReservationData.is_cancelled);
    }
  }, [getReservationData]);

  useEffect(() => {
    const reservationData = safeParse(
      localStorage.getItem("selectedReservationData"),
    );
    if (reservationData) {
      setSelectedReservationData(reservationData);
      setIsReserved(!reservationData.is_cancelled);
    } else {
      setIsReserved(false);
    }
  }, []);

  useEffect(() => {
    if (deepLinkReserve === "1" && selectedSlotData?.id) {
      const isLoggedIn = !!localStorage.getItem("userProfileData");
      if (!isLoggedIn) {
        setShowDeeplinkLoginDialog(true);
      }
    }
  }, [deepLinkReserve, selectedSlotData]);

  useEffect(() => {
    localStorage.setItem("selectedSlotData", JSON.stringify(selectedSlotData));
    if (selectedSlotData?.id) {
      trackDetailView(
        String(selectedSlotData.id),
        selectedSlotData.service_type ?? "WORKSPACE",
      );
    }
  }, [selectedSlotData]);

  const refetchReservationMutation = useMutation({
    mutationFn: () => fetchReservationBySlotId(slotId ?? ""),
    onSuccess: (response: any) => {
      setSelectedReservationData(response);
      setIsReserved(!response.is_cancelled);
      setShowDeeplinkLoginDialog(false);
      if (response?.is_cancelled) {
        handleReserveClick();
      }
    },
    onError: () => {
      setShowDeeplinkLoginDialog(false);
      handleReserveClick();
    },
  });

  const slotReservationMutation = useMutation({
    mutationFn: () => reserveWSMSlot({ slot_id: selectedSlotData?.id }),
    onSuccess: () => {
      setShowAcknowledgementDialog(false);
      trackEvent("reservation_created", {
        slot_id: String(selectedSlotData?.id ?? ""),
        service_type: selectedSlotData?.service_type ?? "",
      });
      const paymentData = {
        title: `${selectedSlotData?.title}`,
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem("paymentSuccessData", JSON.stringify(paymentData));
      if (selectedSlotData?.experience_type === EXPERIENCE_TYPE.FOCUS_HOUSE) {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_SUCCESS_SCREEN, {
          replace: true,
        });
      } else {
        navigate(ROUTE_PATHS.WSM_ALL_SET, { replace: true });
      }
    },
  });

  const cancelReservationMutation = useMutation({
    mutationFn: () =>
      cancelReservation({
        slot_id: selectedSlotData?.id,
        reservation_id: selectedReservationData?.id,
      }),
    onSuccess: (response: any) => {
      setShowReservationCancelPopup(false);
      localStorage.setItem("reservationCancelData", JSON.stringify(response));
      navigate(ROUTE_PATHS.WSM_RESERVATION_CANCEL, { replace: true });
    },
  });

  const handleBackButtonClick = () => {
    if (
      location.state?.fromThankYouScreen ||
      location.state?.fromSuccessScreen ||
      location.key === "default" ||
      !location.key
    ) {
      navigate(ROUTE_PATHS.WSM_HOME, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handleCancelReservationClick = () => {
    setShowReservationCancelPopup(true);
  };

  const handleCancelReservationConfirmClick = () => {
    cancelReservationMutation.mutate();
  };

  const handleOnCompleteLogin = () => {
    refetchReservationMutation.mutate();
  };

  const handleReserveClick = () => {
    trackSlotSelected(
      selectedSlotData?.start_time ?? "",
      selectedSlotData?.slot_date ?? "",
    );
    trackReserveClick(String(selectedSlotData?.id ?? ""));

    if (userProfileData) {
      const leadObj = {
        event_name: selectedSlotData?.title,
        event_date: formatDateString(selectedSlotData?.start_time),
        area: selectedSlotData?.location,
        service_type: selectedSlotData?.service_type,
        shift: getWorkspaceSlotDuration(
          selectedSlotData?.start_time,
          selectedSlotData?.end_time,
        ),
        timing: formatWorkspaceTimeRange(
          selectedSlotData?.start_time,
          selectedSlotData?.end_time,
        ),
        experience_type: selectedSlotData?.experience_type,
        name: `${userProfileData.first_name ?? ""} ${userProfileData.last_name ?? ""}`,
        email: userProfileData.email ?? "",
        phone: userProfileData.mobile_number ?? "",
        profile_link: userProfileData.profile_link ?? "",
        is_founder: userProfileData.is_founder ? "True" : "False",
        role: userProfileData.role ?? "",
        is_member: isActiveMember ? "True" : "False",
        lookup_key: `${userProfileData.id}_${selectedSlotData?.id}`,
      };
      if (process.env.NODE_ENV === "production") {
        sendEventReserveLeadMutation.mutate(leadObj);
      }

      // Check for free Pass
      if (selectedSlotData?.is_free_slot) {
        setShowAcknowledgementDialog(true);
        return;
      }

      if (!userProfileData.is_founder && selectedSlotData?.only_for_founders) {
        setShowFounderOnlyPopup(true);
        return;
      }

      // Check membership validity for Reservation
      const membershipEndDate = new Date(
        userProfileData?.credit?.membership_end_date,
      );
      const slotEndDate = new Date(selectedSlotData.end_time);

      if (slotEndDate > membershipEndDate && isActiveMember) {
        setShowInformationPopup(true);
        return;
      }

      // Handle Slot Reservation
      if (selectedSlotData?.is_exclusive) {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
      } else if (
        isActiveMember &&
        checkNonMemberCredits(userProfileData?.credit, selectedSlotData)
      ) {
        setShowAcknowledgementDialog(true);
      } else {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
      }
    } else {
      if (slotId) {
        setShowDeeplinkLoginDialog(true);
      }
    }
  };

  const handleReservationConfirmClick = () => {
    slotReservationMutation.mutate();
  };

  const openWhatsappChannel = () => {
    handleAppExit(WEB_LINK.WHATSAPP_CHANNEL);
  };

  const getExclusiveSlotReserveButton = () => {
    const isAvailableForPurchase = isActiveMember
      ? selectedSlotData?.purchase_access?.includes(
          userProfileData?.credit?.membership?.id,
        )
      : false;
    let availableForMembershipName = null;

    if (selectedSlotData?.purchase_access?.length > 0) {
      const availableForMembership = membershipListData?.data?.content?.find(
        (membership: any) =>
          membership.id === selectedSlotData?.purchase_access[0],
      );
      availableForMembershipName = MEMBERSHIP_TYPE.find(
        (type) => type.value === availableForMembership?.membership_type,
      )?.name;
    }

    return (
      <div className="workspace-detail-screen-footer-container">
        {availableForMembershipName && !isAvailableForPurchase && (
          <div className="info-container">
            <div className="text-container">
              <span>Available only for</span>
              <span style={{ color: "#C1F700" }}>
                {`${availableForMembershipName} membership.`}
              </span>
            </div>
            <img src={paymentInfoIcon} alt="payment-info-icon" />
          </div>
        )}
        <div
          className="button-container"
          onClick={() => {
            if (!isAvailableForPurchase) return;
            handleReserveClick();
          }}
          style={{
            backgroundColor: isAvailableForPurchase ? "white" : "darkgray",
            cursor: isAvailableForPurchase ? "pointer" : "not-allowed",
          }}
        >
          <div className="left-container">Reserve Now</div>
          <div className="right-container">
            <div className="divider"></div>
            <div className="text-container">
              <span>
                {isAvailableForPurchase ? "Exclusive for you" : "Exclusive"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleShareClick = async () => {
    const pageUrl = window.location.href;
    const shareText = `Founders don’t grow in noise.
Step into a better room at The Foundry:
`;
    try {
      if (navigator.share) {
        await navigator.share({
          url: slotId,
          text: shareText,
        });
      } else {
        await navigator.clipboard.writeText(pageUrl);
        alert("Copied to clipboard!");
      }
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  const getFocusHouseStage = () => {
    if (selectedReservationData?.fourth_stage_selected_options) {
      return 4;
    } else if (isEventStarted(selectedReservationData?.slot_start_time)) {
      return 3;
    } else if (selectedReservationData?.first_stage_selected_options) {
      return 2;
    } else {
      return 1;
    }
  };

  const getFocusHouseStageCTA = () => {
    if (selectedReservationData?.fourth_stage_selected_options) {
      return "Let’s Reflect Now";
    } else if (isEventStarted(selectedReservationData?.slot_start_time)) {
      return "Meet Your Guide";
    } else if (selectedReservationData?.first_stage_selected_options) {
      return "See you at SabeHub";
    } else {
      return "Set Your Intention";
    }
  };

  const handleFocusHouseSetupClick = async () => {
    if (selectedReservationData?.first_stage_selected_options) {
      if (isEventStarted(selectedReservationData?.slot_start_time)) {
        navigate(
          ROUTE_PATHS.WSM_CALENDAR_PASS_INTRODUCTION_SCREEN.replace(
            ":stage",
            "4",
          ),
        );
      }
    } else if (selectedReservationData?.fourth_stage_selected_options) {
    } else {
      navigate(
        ROUTE_PATHS.WSM_CALENDAR_PASS_INTRODUCTION_SCREEN.replace(
          ":stage",
          "1",
        ),
      );
    }
  };

  return (
    <div className="wms-mobile-container">
      {slotReservationMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {slotReservationMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="workspace-detail-screen-container">
        <div className="workspace-detail-header-container">
          {userProfileData ? (
            <img
              src={backIcon}
              alt="dropdown-logo"
              className="image"
              onClick={handleBackButtonClick}
            />
          ) : (
            <img src={appIcon} alt="app-logo" className="app-image" />
          )}
          <div className="right-container">
            <div className="title">{selectedSlotData?.title}</div>
          </div>
        </div>
        {selectedSlotData?.slot_image && (
          <div
            className="workspace-detail-slot-image-container"
            style={{ backgroundImage: `url(${selectedSlotData?.slot_image})` }}
          ></div>
        )}
        {selectedSlotData?.experience_type === EXPERIENCE_TYPE.FOCUS_HOUSE &&
          isReserved && (
            <div className="workspace-detail-container">
              <div className="header-bar-container">
                <span className="detail-title">
                  Focus House <span style={{ color: "#ff6b35" }}>Journey</span>
                </span>
              </div>
              <StageProgressView
                current_stage={getFocusHouseStage()}
              ></StageProgressView>
              <div
                className="setup-button"
                onClick={handleFocusHouseSetupClick}
              >
                <>
                  {getFocusHouseStageCTA()}
                  <img src={setupArrow} alt="focus-house-setup-arrow" />
                </>
              </div>
            </div>
          )}
        <div className="workspace-detail-date-location-container">
          <div className="header-container">
            <div className="header-title">
              <span>
                {selectedSlotData?.short_description ?? selectedSlotData?.title}
              </span>
              {(selectedSlotData?.service_type ===
                CALENDAR_RESERVATION_TYPE.DF_WORKSPACE ||
                selectedSlotData?.service_type ===
                  CALENDAR_RESERVATION_TYPE.MEETING_ROOMS) && (
                <span className="header-title-sub-text">{`Upto ${selectedSlotData?.slot_capacity ?? 0} member`}</span>
              )}
            </div>
            <img
              src={eventShareIcon}
              alt="share-event-icon"
              className="share-image"
              onClick={handleShareClick}
            />
          </div>
          <div className="detail-container">
            <div className="image">
              <img src={calendarIcon} alt="calendar-logo" />
            </div>
            <div>
              <div className="title">
                {formatDateString(selectedSlotData?.start_time)}
              </div>
              <div className="date-time">
                {formatWorkspaceTimeRange(
                  selectedSlotData?.start_time,
                  selectedSlotData?.end_time,
                )}
              </div>
            </div>
          </div>
          <div className="divider"></div>
          <div className="detail-container">
            <div className="image">
              <img src={locationIcon} alt="location-logo" />
            </div>
            <div className="right-container">
              <div>
                <div className="title">{selectedSlotData?.location}</div>
                {selectedSlotData?.sub_location && (
                  <div className="date-time">
                    {selectedSlotData?.sub_location}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleAppExit(selectedSlotData?.location_url)}
              >
                Direction
              </button>
            </div>
          </div>
        </div>

        {selectedSlotData?.description && (
          <div className="workspace-detail-container">
            <div className="header-bar-container">
              <span className="detail-title">
                About the <span style={{ color: "#ff6b35" }}>session</span>
              </span>
              {selectedSlotData?.landing_url && (
                <button
                  onClick={() => {
                    navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
                      state: {
                        anyData: {
                          title:
                            CALENDAR_RESERVATION_TABS.find(
                              (type) =>
                                type.value === selectedSlotData?.service_type,
                            )?.name || "",
                          url: selectedSlotData?.landing_url,
                        },
                      },
                    });
                  }}
                >
                  Learn more
                </button>
              )}
            </div>
            <div className="divider"></div>
            <div
              className="sub-title"
              dangerouslySetInnerHTML={{
                __html: selectedSlotData?.description || "",
              }}
            ></div>
          </div>
        )}

        {selectedSlotData?.instructor && (
          <div className="workspace-detail-container">
            <div className="header-bar-container">
              <span className="detail-title">
                meet ur{" "}
                <span style={{ color: "#ff6b35" }}>
                  {selectedSlotData?.instructor?.role ?? "Guest"}
                </span>
              </span>
              {selectedSlotData?.instructor?.professional_link && (
                <button
                  onClick={() => {
                    handleAppExit(
                      selectedSlotData?.instructor?.professional_link,
                    );
                  }}
                >
                  Learn more
                </button>
              )}
            </div>
            <div className="divider"></div>
            <div className="workspace-instructor-container">
              <div className="image-container">
                <img
                  src={selectedSlotData?.instructor?.profile_pic}
                  alt="workspace_instructor_image"
                />
              </div>
              <div className="left-container">
                <div className="header-container">
                  <span className="title">
                    {selectedSlotData?.instructor?.first_name}
                  </span>
                  <span className="sub-title">
                    {" "}
                    {selectedSlotData?.instructor?.last_name}
                  </span>
                </div>
                <div className="detail-container">
                  <span>{selectedSlotData?.instructor?.about}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="workspace-detail-contact-detail-container">
          <ImageCarousel
            images={
              selectedSlotData?.real_life_images?.length > 0
                ? selectedSlotData?.real_life_images
                : getCalendarRealLifeImages(selectedSlotData?.service_type)
            }
            title="In Real"
            subTitle="Life"
          />
        </div>

        {selectedSlotData?.venue && (
          <div className="workspace-detail-contact-detail-container">
            {selectedSlotData?.venue?.real_life_images?.length > 0 && (
              <ImageCarousel
                images={selectedSlotData?.venue?.real_life_images}
                title="UR"
                subTitle="Venue"
              />
            )}
            <div className="detail-container">
              {selectedSlotData?.venue?.description}
            </div>
          </div>
        )}

        <div className="workspace-detail-contact-detail-container">
          <div className="footer-container">
            <div className="title">Contact Details</div>
            <div className="sub-container">
              <img src={appTextIcon} alt="sabe-text-logo" />
              <img
                src={whatsappIcon}
                alt="whatsapp-logo"
                onClick={openWhatsappChannel}
                style={{ cursor: "pointer" }}
              />
            </div>
          </div>
        </div>

        <ExpandableView title="Terms &" subTitle="Policies">
          <div className="workspace-expanded-detail-container">
            <div className="divider"></div>
            <div className="sub-container">
              <div className="sub-title">
                {getTermaAndConditionsText(selectedSlotData?.service_type)}
              </div>
            </div>
          </div>
        </ExpandableView>
        {selectedSlotData?.is_exclusive && !isReserved ? (
          getExclusiveSlotReserveButton()
        ) : selectedReservationData && selectedReservationData?.is_cancelled ? (
          <div className="workspace-detail-screen-footer-container">
            <div className={`button-container disabled`}>
              <div className="left-container">Cancelled</div>
            </div>
          </div>
        ) : isReserved ? (
          <>
            <div className="workspace-detail-screen-footer-container">
              <div className="reserved-parent-container">
                <div className="reserved-button-container">
                  <img src={tickIcon} alt="reserved-tick-button" />
                  Reserved
                </div>
                <div
                  className="share-button-container"
                  onClick={handleShareClick}
                >
                  <img src={shareIcon} alt="reserved-share-button" />
                  Share
                </div>
              </div>
            </div>
            {!isEventStarted(selectedSlotData?.start_time) && (
              <div
                className="workspace-detail-screen-cancel-reservation-container"
                onClick={handleCancelReservationClick}
              >
                Cancel
              </div>
            )}
          </>
        ) : (
          <div className="workspace-detail-screen-footer-container">
            <div className="button-container" onClick={handleReserveClick}>
              <div className="left-container">Reserve Now</div>
            </div>
          </div>
        )}
      </div>
      <ReserveAcknowledgementDialog
        open={showAcknowledgementDialog}
        onClose={() => setShowAcknowledgementDialog(false)}
        slotData={selectedSlotData}
        onClickLetsDo={handleReservationConfirmClick}
        isLoading={slotReservationMutation?.isPending}
      />
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
      <InformationPopup
        open={showInformationPopup}
        onClose={() => setShowInformationPopup(false)}
        onClick={() => {
          setShowInformationPopup(false);
          navigate(-1);
          setSelectedTabBar(MENU_ITEM.MEMBERSHIP);
        }}
        title={STRING.MEMBERSHIP_EXPIRE_INFO_TITLE}
        subTitle={STRING.MEMBERSHIP_EXPIRE_INFO_SUB_TITLE}
        buttonText={STRING.MEMBERSHIP_EXPIRE_INFO_BUTTON_TITLE}
      />
      <InformationPopup
        open={showFounderOnlyPopup}
        onClose={() => setShowFounderOnlyPopup(false)}
        onClick={() => {}}
        title={STRING.FOUNDER_ONLY_POPUP_TITLE}
        subTitle={STRING.FOUNDER_ONLY_POPUP_SUB_TITLE}
        buttonText={STRING.FOUNDER_ONLY_POPUP_BUTTON_TITLE}
      />
      <CancelReservationPopup
        open={showReservationCancelPopup}
        reservationData={selectedReservationData}
        onClose={() => setShowReservationCancelPopup(false)}
        onClick={handleCancelReservationConfirmClick}
      />
      <DeeplinkLoginDialog
        open={showDeeplinkLoginDialog}
        isFocusHousePass={
          selectedSlotData?.experience_type === EXPERIENCE_TYPE.FOCUS_HOUSE
        }
        onClose={() => setShowDeeplinkLoginDialog(false)}
        onCompleteLogin={handleOnCompleteLogin}
      />
    </div>
  );
};

export default WorkspaceDetailScreen;
